import { config } from '@/utilities/config'
import { guardPatientSessionLinkClick } from '@/utilities/auth/expireBrowserSession'
import { isAuthPagePath } from '@/utilities/paths'

function matchesAnyPrefix(pathname: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => pathname.startsWith(prefix))
}

/**
 * Pages that only a signed-in patient can open. Anonymous pages (kit registration, welcome,
 * HIPAA consent) are excluded, because a patient can start those without a session.
 */
function isSignedInOnlyPatientPath(pathname: string): boolean {
  return (
    !config.isPublicPatientPath(pathname) &&
    !matchesAnyPrefix(pathname, config.onboardingAllowlist) &&
    !matchesAnyPrefix(pathname, config.hipaaConsentAllowlist) &&
    !isAuthPagePath(pathname)
  )
}

function handleDocumentClick(event: MouseEvent): void {
  if (event.defaultPrevented || event.button !== 0) return
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

  const target = event.target instanceof Element ? event.target : null
  const anchor = target?.closest('a[href]')
  if (!(anchor instanceof HTMLAnchorElement)) return
  if ((anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return

  const destination = new URL(anchor.href, window.location.origin)
  if (destination.origin !== window.location.origin) return
  if (!isSignedInOnlyPatientPath(window.location.pathname)) return
  // Same rule the proxy uses: public paths such as logout never need a session.
  if (config.isPublicPatientPath(destination.pathname)) return

  const returnPath = `${destination.pathname}${destination.search}${destination.hash}`
  if (!guardPatientSessionLinkClick(event, returnPath)) {
    // Stop the link from reaching the router, so the session-expired redirect is the only navigation.
    event.stopPropagation()
  }
}

/**
 * Guards every link a signed-in patient follows from a signed-in page. When the Stytch session
 * cookie is gone, the click ends the session with the session-expired reason and returns the
 * patient to the link's page after sign-in. Installs on the capture phase so it runs before
 * router handlers. Returns a cleanup function.
 */
export function installPatientLinkSessionGuard(): () => void {
  document.addEventListener('click', handleDocumentClick, true)
  return () => document.removeEventListener('click', handleDocumentClick, true)
}
